import { useToastsStore } from "@stores/toasts";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import type { BuildInfo } from "@helpers/RemoteLogger";
import { fetchBuildInfo } from "@composables/useBuildInfo";

const ONE_SECOND_MS = 1000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

// How often to check for a new version
let POLL_INTERVAL_MS = 30 * ONE_MINUTE_MS;
// How long to wait after finding a new version before showing a warning
let NEW_VERSION_WARNING_THRESHOLD_MS = 2 * ONE_HOUR_MS;
// How long to wait after showing a new version warning before forcing a refresh
let NEW_VERSION_BUFFER_UNTIL_FORCE_REFRESH_MS = 30 * ONE_MINUTE_MS;
// We force a refresh after so long (regardless of version)
let TOTAL_UPTIME_LIMIT_MS = 3 * ONE_DAY_MS;
// How long to wait before forcing a refresh (after showing a warning)
let REFRESH_FINAL_WARNING_MS = 1 * ONE_MINUTE_MS;

class VersionChecker {
  private currentBuildInfo: BuildInfo | null;
  private appStartTime: number;
  private newVersionWarningWasShown: boolean = false;
  private pollInterval: NodeJS.Timeout | null = null;
  private forceRefreshWarningShown: boolean = false;

  constructor(currentBuildInfo: BuildInfo | null) {
    /* Testing
    POLL_INTERVAL_MS = 10 * ONE_SECOND_MS;
    NEW_VERSION_WARNING_THRESHOLD_MS = 60 * ONE_SECOND_MS;
    NEW_VERSION_BUFFER_UNTIL_FORCE_REFRESH_MS = 30 * ONE_SECOND_MS;
    TOTAL_UPTIME_LIMIT_MS = 1 * ONE_HOUR_MS;
    */

    this.currentBuildInfo = currentBuildInfo;
    this.appStartTime = Date.now();
  }

  private initiateForceRefresh(reason: string): void {
    if (this.forceRefreshWarningShown) return;

    this.forceRefreshWarningShown = true;
    const toastsStore = useToastsStore();
    const remoteLogger = getGlobalRemoteLogger();

    toastsStore.warn({
      message: "Updating the Application",
      details: `Please save your work! The app will update shortly.`,
      expiresInMs: null,
    });

    const message = `VersionChecker.ForceRefreshWarning: ${reason}`;
    console.log(message);
    remoteLogger?.info(message);

    // Give them a final warning and then refresh
    setTimeout(() => {
      const refreshMessage = `VersionChecker.ForceRefreshNow: ${reason}`;
      console.log(refreshMessage);
      remoteLogger?.info(refreshMessage);
      window.location.reload();
    }, REFRESH_FINAL_WARNING_MS);
  }

  async checkVersion(): Promise<void> {
    const toastsStore = useToastsStore();
    const remoteLogger = getGlobalRemoteLogger();

    try {
      const latestBuildInfo = await fetchBuildInfo();

      if (!latestBuildInfo) {
        const message = "VersionChecker.Failed: Could not fetch version.json";
        console.log(message);
        remoteLogger?.info(message);
        return;
      }
      const currentUptime = Date.now() - this.appStartTime;
      const currentUptimeInHours = currentUptime / ONE_HOUR_MS;
      // Check if version has changed
      const versionChangedSinceLoad =
        this.currentBuildInfo && (latestBuildInfo.buildTimestamp !== this.currentBuildInfo.buildTimestamp || latestBuildInfo.gitCommitHash !== this.currentBuildInfo.gitCommitHash);
      // output build info
      const buildInfoString = `versionChanged=${versionChangedSinceLoad}, currentUptime=${currentUptimeInHours.toFixed(3)}h latestBuildInfo=${JSON.stringify(latestBuildInfo)}`;
      console.log(buildInfoString);

      // Force refresh aftersome period regardless of version
      if (currentUptime > TOTAL_UPTIME_LIMIT_MS) {
        this.initiateForceRefresh("LongRunning");
        return;
      }

      if (versionChangedSinceLoad) {
        const timeSinceLatestVersionBuild = Date.now() - latestBuildInfo.buildTimestamp;
        const timeSinceLatestVersionBuildInHours = timeSinceLatestVersionBuild / ONE_HOUR_MS;
        const canShowNewVersionWarning = timeSinceLatestVersionBuild > NEW_VERSION_WARNING_THRESHOLD_MS;
        const message = `VersionChecker.VersionChanged! current=${this.currentBuildInfo?.gitCommitHash} latest=${latestBuildInfo.gitCommitHash} timeSinceLatestBuild=${timeSinceLatestVersionBuildInHours.toFixed(3)}h showWarning=${canShowNewVersionWarning} warningShown=${this.newVersionWarningWasShown}`;
        console.info(message);

        if (canShowNewVersionWarning && !this.newVersionWarningWasShown) {
          // Show warning toast for old version
          this.newVersionWarningWasShown = true;
          toastsStore.warn({
            message: "New Version Available!",
            details: "Refresh your browser to get the latest features and fixes.",
            expiresInMs: 10 * ONE_MINUTE_MS,
          });

          remoteLogger?.info("VersionChecker.VersionChangedToast", false, false, {
            currentHash: this.currentBuildInfo.gitCommitHash,
            latestHash: latestBuildInfo.gitCommitHash,
            uptimeMs: currentUptime.toString(),
          });
          return;
        }
        // If there's a new version and we've warned them, eventually force a refresh
        if (timeSinceLatestVersionBuild > NEW_VERSION_BUFFER_UNTIL_FORCE_REFRESH_MS + NEW_VERSION_WARNING_THRESHOLD_MS) {
          // Force refresh if they've been on old version for too long and ignored the warning
          this.initiateForceRefresh("NewVersion");
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      // This happens often enough (and isn't critical) that we don't want to spam the logs
      remoteLogger?.info("VersionChecker:CheckFailed: " + errorMessage, false, false, {
        errorMessage,
      });
    }
  }

  startVersionChecking(): void {
    const remoteLogger = getGlobalRemoteLogger();

    if (!this.currentBuildInfo) {
      remoteLogger?.warn("VersionChecker.NoCurrentBuildInfo");
      return;
    }

    // Start polling
    this.pollInterval = setInterval(() => this.checkVersion(), POLL_INTERVAL_MS);

    remoteLogger?.info("VersionChecker.Started", false, false, {
      pollIntervalMs: POLL_INTERVAL_MS.toString(),
      warningThresholdMs: NEW_VERSION_WARNING_THRESHOLD_MS.toString(),
      forceRefreshMs: TOTAL_UPTIME_LIMIT_MS.toString(),
    });
  }

  stopVersionChecking(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}

export function createVersionChecker(currentBuildInfo: BuildInfo | null): VersionChecker {
  return new VersionChecker(currentBuildInfo);
}
